"use client";

import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslations } from "next-intl";
import {
  ChevronDown,
  ChevronRight,
  TextCursorInput,
  Trash,
  ArrowRight,
  ArrowRightLeft,
  UserMinus,
  LogIn
} from "lucide-react";
import {
  BreakoutAssignmentModeFieldset,
  BreakoutParticipantMediaIcons,
  dialogButtonSecondary,
  dialogFieldInput,
  type AssignmentMode
} from "./breakoutRoomsDialogShared";
import { resolveBreakoutTheme } from "@client/utils/breakoutThemes";
import { MeetUserAvatar } from "@client/components/meet/MeetUserAvatar";

type BreakoutRoomRow = {
  id: string;
  name: string;
  themeColor?: string | null;
  assignments: Array<{ id: string; userId?: number | null; guestId?: string | null; state: string }>;
};

type BreakoutParticipant = {
  id: string;
  userId?: number | null;
  guestId?: string | null;
  name?: string | null;
  userName: string;
  isGuest: boolean;
  waitingRoomHeld: boolean;
  isMeetingHost?: boolean;
  avatarUrl?: string | null;
};

type RoomActivity = {
  id: string;
  participants: Array<{
    identity: string;
    name: string;
    tracks: Array<{ type: number; source: number; muted: boolean }>;
  }>;
};

const dialogIconButton =
  "inline-flex size-9 shrink-0 cursor-pointer items-center justify-center rounded-lg text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2";

const participantActionButton =
  "inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-800 shadow-sm transition-colors hover:border-gray-300 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 disabled:cursor-not-allowed disabled:opacity-50";

function BreakoutFloatingMenu({
  open,
  anchorRef,
  children,
  minWidth = 176,
  maxHeight
}: {
  open: boolean;
  anchorRef: React.RefObject<HTMLButtonElement | null>;
  children: React.ReactNode;
  minWidth?: number;
  maxHeight?: number;
}) {
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);

  useLayoutEffect(() => {
    if (!open || !anchorRef.current) {
      setPosition(null);
      return;
    }
    const update = () => {
      const rect = anchorRef.current?.getBoundingClientRect();
      if (!rect) return;
      let left = rect.right - minWidth;
      left = Math.max(8, Math.min(left, window.innerWidth - minWidth - 8));
      setPosition({ top: rect.bottom + 4, left });
    };
    update();
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [open, anchorRef, minWidth]);

  if (!open || !position || typeof document === "undefined") return null;

  return createPortal(
    <div
      data-breakout-floating-menu=""
      className="fixed z-[200] overflow-hidden rounded-lg border border-gray-200 bg-white py-1 shadow-lg"
      style={{
        top: position.top,
        left: position.left,
        minWidth,
        maxHeight: maxHeight ?? undefined,
        overflowY: maxHeight ? "auto" : undefined
      }}
    >
      {children}
    </div>,
    document.body
  );
}
const roomActionIconButton =
  "inline-flex size-9 shrink-0 cursor-pointer items-center justify-center rounded-lg text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 focus-visible:ring-offset-1";

const roomDeleteIconButton =
  "inline-flex size-9 shrink-0 cursor-pointer items-center justify-center rounded-lg text-red-700 transition-colors hover:bg-red-50 hover:text-red-800 disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-200 focus-visible:ring-offset-1";

function participantKey(person: BreakoutParticipant): string {
  return person.userId ? String(person.userId) : `guest:${person.guestId}`;
}

function participantDisplayName(person: BreakoutParticipant): string {
  return person.name || person.userName || "Participant";
}

function resolvePersonRoomId(
  person: BreakoutParticipant,
  rooms: BreakoutRoomRow[],
  assignmentMap: Record<string, string>
): string | undefined {
  const key = participantKey(person);
  if (assignmentMap[key]) return assignmentMap[key];
  for (const room of rooms) {
    for (const assignment of room.assignments) {
      if (person.userId && assignment.userId === person.userId) return room.id;
      if (person.guestId && assignment.guestId === person.guestId) return room.id;
    }
  }
  return undefined;
}

function assignmentForPersonInRoom(
  person: BreakoutParticipant,
  room: BreakoutRoomRow
): BreakoutRoomRow["assignments"][number] | undefined {
  return room.assignments.find(
    (assignment) =>
      (person.userId != null && assignment.userId === person.userId) ||
      (person.guestId != null && assignment.guestId === person.guestId)
  );
}

function isParticipantJoinedInRoom(person: BreakoutParticipant, room: BreakoutRoomRow): boolean {
  return assignmentForPersonInRoom(person, room)?.state === "joined";
}

function findActivityForPersonInRoom(
  person: BreakoutParticipant,
  roomId: string,
  activities: RoomActivity[]
): { room: RoomActivity; live: RoomActivity["participants"][number] } | null {
  for (const room of activities) {
    if (room.id !== roomId) continue;
    for (const live of room.participants) {
      if (person.userId && live.identity.startsWith("USER-") && live.identity.endsWith(`-${person.userId}`)) {
        return { room, live };
      }
      if (person.guestId && live.identity.startsWith("GUEST-") && live.identity.endsWith(`-${person.guestId}`)) {
        return { room, live };
      }
    }
  }
  return null;
}

function participantAvatarVariant(person: BreakoutParticipant): "default" | "host" | "guest" {
  if (person.isMeetingHost) return "host";
  if (person.isGuest) return "guest";
  return "default";
}

function BreakoutParticipantRoomActions({
  person,
  currentRoomId,
  rooms,
  roomNames,
  assignments,
  eligibleParticipants,
  busy,
  disabled,
  onMoveParticipant,
  onExchangeParticipants,
  onUnassignParticipant
}: {
  person: BreakoutParticipant;
  currentRoomId: string;
  rooms: BreakoutRoomRow[];
  roomNames: Record<string, string>;
  assignments: Record<string, string>;
  eligibleParticipants: BreakoutParticipant[];
  busy: boolean;
  disabled: boolean;
  onMoveParticipant: (person: BreakoutParticipant, roomId: string) => void;
  onExchangeParticipants: (person: BreakoutParticipant, other: BreakoutParticipant) => void;
  onUnassignParticipant: (person: BreakoutParticipant) => void;
}) {
  const tBreakout = useTranslations("breakoutRooms");
  const [openMenu, setOpenMenu] = useState<"move" | "exchange" | null>(null);
  const moveButtonRef = useRef<HTMLButtonElement>(null);
  const exchangeButtonRef = useRef<HTMLButtonElement>(null);
  const name = participantDisplayName(person);

  useEffect(() => {
    if (!openMenu) return;
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (moveButtonRef.current?.contains(target) || exchangeButtonRef.current?.contains(target)) return;
      if (target instanceof Element && target.closest("[data-breakout-floating-menu]")) return;
      setOpenMenu(null);
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [openMenu]);

  const otherRooms = rooms.filter((room) => room.id !== currentRoomId);
  const exchangeGroups = rooms
    .filter((room) => room.id !== currentRoomId)
    .map((room) => ({
      room,
      people: eligibleParticipants.filter(
        (candidate) => candidate.id !== person.id && resolvePersonRoomId(candidate, rooms, assignments) === room.id
      )
    }))
    .filter((group) => group.people.length > 0);

  return (
    <div className="flex shrink-0 flex-wrap items-center justify-end gap-1.5">
      {currentRoomId ? (
        <button
          type="button"
          className={participantActionButton}
          title={tBreakout("unassignTitle")}
          disabled={disabled || busy}
          onClick={() => onUnassignParticipant(person)}
        >
          <UserMinus className="size-3.5 shrink-0" aria-hidden />
          {tBreakout("unassignAction")}
        </button>
      ) : null}
      {otherRooms.length > 0 && (
        <>
          <button
            ref={moveButtonRef}
            type="button"
            className={`${participantActionButton} ${openMenu === "move" ? "border-gray-300 bg-gray-50" : ""}`}
            aria-expanded={openMenu === "move"}
            aria-haspopup="menu"
            title="Move to another room"
            disabled={disabled || busy}
            onClick={() => setOpenMenu((menu) => (menu === "move" ? null : "move"))}
          >
            <ArrowRight className="size-3.5 shrink-0" aria-hidden />
            Move to
          </button>
          <BreakoutFloatingMenu open={openMenu === "move"} anchorRef={moveButtonRef}>
            <div role="menu" aria-label={`Move ${name} to room`}>
              {otherRooms.map((target) => (
                <button
                  key={target.id}
                  type="button"
                  role="menuitem"
                  className="flex w-full cursor-pointer px-3 py-2 text-left text-sm text-gray-900 hover:bg-gray-100 focus-visible:bg-gray-100 focus-visible:outline-none"
                  onClick={() => {
                    onMoveParticipant(person, target.id);
                    setOpenMenu(null);
                  }}
                >
                  {roomNames[target.id] ?? target.name}
                </button>
              ))}
            </div>
          </BreakoutFloatingMenu>
        </>
      )}
      {exchangeGroups.length > 0 && (
        <>
          <button
            ref={exchangeButtonRef}
            type="button"
            className={`${participantActionButton} ${openMenu === "exchange" ? "border-gray-300 bg-gray-50" : ""}`}
            aria-expanded={openMenu === "exchange"}
            aria-haspopup="menu"
            title="Exchange with another participant"
            disabled={disabled || busy}
            onClick={() => setOpenMenu((menu) => (menu === "exchange" ? null : "exchange"))}
          >
            <ArrowRightLeft className="size-3.5 shrink-0" aria-hidden />
            Exchange
          </button>
          <BreakoutFloatingMenu
            open={openMenu === "exchange"}
            anchorRef={exchangeButtonRef}
            minWidth={224}
            maxHeight={256}
          >
            <div role="menu" aria-label={`Exchange ${name} with another participant`}>
              {exchangeGroups.map(({ room, people }) => (
                <div key={room.id} role="none">
                  <div className="flex items-center justify-between bg-gray-50 px-3 py-1.5 text-xs font-semibold text-gray-800">
                    <span>{roomNames[room.id] ?? room.name}</span>
                    <span className="tabular-nums text-primary">{people.length}</span>
                  </div>
                  {people.map((other) => (
                    <button
                      key={other.id}
                      type="button"
                      role="menuitem"
                      className="flex w-full cursor-pointer px-3 py-2 pl-4 text-left text-sm text-gray-900 hover:bg-gray-100 focus-visible:bg-gray-100 focus-visible:outline-none"
                      onClick={() => {
                        onExchangeParticipants(person, other);
                        setOpenMenu(null);
                      }}
                    >
                      {participantDisplayName(other)}
                    </button>
                  ))}
                </div>
              ))}
            </div>
          </BreakoutFloatingMenu>
        </>
      )}
    </div>
  );
}

export interface BreakoutRoomsManagerListProps {
  sessionStatus: "not_started" | "open" | "closing" | "closed";
  rooms: BreakoutRoomRow[];
  roomNames: Record<string, string>;
  assignments: Record<string, string>;
  assignmentMode: AssignmentMode;
  eligibleParticipants: BreakoutParticipant[];
  roomActivity: RoomActivity[];
  busy: boolean;
  settingsOpen: boolean;
  countdownSeconds: number;
  autoCloseMinutes: number;
  allowReturnToMain: boolean;
  allowParticipantMove: boolean;
  sessionEndsAt: string | null;
  collapsedRoomIds: Record<string, boolean>;
  renamingRoomId: string | null;
  closeCountdownRemaining: number | null;
  sessionTimerRemainingSec: number | null;
  participantsPerRoomHint: number;
  assignableParticipantCount: number;
  onSetRoomNames: (updater: (current: Record<string, string>) => Record<string, string>) => void;
  onSetAssignmentMode: (mode: AssignmentMode) => void;
  onSetCountdownSeconds: (value: number) => void;
  onSetAutoCloseMinutes: (value: number) => void;
  onSetAllowReturnToMain: (value: boolean) => void;
  onSetAllowParticipantMove: (value: boolean) => void;
  onToggleRoomCollapsed: (roomId: string) => void;
  onSetRenamingRoomId: (roomId: string | null) => void;
  onDeleteRoom: (roomId: string) => void;
  onRenameRoom: (roomId: string) => void;
  onJoinRoom: (roomId: string) => void;
  onMoveParticipant: (person: BreakoutParticipant, roomId: string) => void;
  onExchangeParticipants: (person: BreakoutParticipant, other: BreakoutParticipant) => void;
  onUnassignParticipant: (person: BreakoutParticipant) => void;
  onExtendSessionTimer: () => void;
}

export function BreakoutRoomsManagerList({
  sessionStatus,
  rooms,
  roomNames,
  assignments,
  assignmentMode,
  eligibleParticipants,
  roomActivity,
  busy,
  settingsOpen,
  countdownSeconds,
  autoCloseMinutes,
  allowReturnToMain,
  allowParticipantMove,
  sessionEndsAt,
  collapsedRoomIds,
  renamingRoomId,
  closeCountdownRemaining,
  sessionTimerRemainingSec,
  participantsPerRoomHint,
  assignableParticipantCount,
  onSetRoomNames,
  onSetAssignmentMode,
  onSetCountdownSeconds,
  onSetAutoCloseMinutes,
  onSetAllowReturnToMain,
  onSetAllowParticipantMove,
  onToggleRoomCollapsed,
  onSetRenamingRoomId,
  onDeleteRoom,
  onRenameRoom,
  onJoinRoom,
  onMoveParticipant,
  onExchangeParticipants,
  onUnassignParticipant,
  onExtendSessionTimer
}: BreakoutRoomsManagerListProps) {
  const t = useTranslations();
  const isRoomCollapsed = (roomId: string) => collapsedRoomIds[roomId] === true;
  const participantsInRoom = (roomId: string) =>
    eligibleParticipants.filter((person) => resolvePersonRoomId(person, rooms, assignments) === roomId);
  const unassignedParticipants = () =>
    eligibleParticipants.filter((person) => !resolvePersonRoomId(person, rooms, assignments));

  const showMoveControls =
    sessionStatus === "open" || (sessionStatus === "not_started" && assignmentMode !== "self_select");

  return (
    <>
      {(closeCountdownRemaining !== null || sessionTimerRemainingSec !== null) && (
        <div className="mb-4 flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-700" aria-live="polite">
          {closeCountdownRemaining !== null && (
            <span className="font-medium text-amber-800">Closing in {closeCountdownRemaining}s</span>
          )}
          {sessionTimerRemainingSec !== null && (
            <span className="font-medium text-amber-800">
              {Math.floor(sessionTimerRemainingSec / 60)}:{String(sessionTimerRemainingSec % 60).padStart(2, "0")}{" "}
              remaining
            </span>
          )}
        </div>
      )}

      {sessionStatus === "closing" && (
        <p className="mb-4 text-sm text-gray-600">
          Breakout rooms are closing. Participants will return to the main meeting.
        </p>
      )}

      {(sessionStatus === "not_started" || sessionStatus === "open") && settingsOpen && (
        <div id="breakout-settings-panel" className="mb-4 space-y-4 rounded-xl border border-gray-200 bg-gray-50 p-4">
          {sessionStatus === "not_started" && (
            <>
              {(assignmentMode === "auto" || assignmentMode === "manual") && rooms.length > 0 && (
                <p className="text-sm text-gray-600">
                  ~{participantsPerRoomHint} people per room ({assignableParticipantCount} people, {rooms.length}{" "}
                  rooms).
                </p>
              )}
              <BreakoutAssignmentModeFieldset
                name="breakout-assignment-manage"
                value={assignmentMode}
                onChange={onSetAssignmentMode}
                disabled={busy}
              />
              <div className="flex flex-wrap gap-4 text-sm text-gray-800">
                <label className="flex min-h-11 cursor-pointer items-center gap-2">
                  <input
                    type="checkbox"
                    className="size-4 rounded border-gray-400"
                    checked={allowReturnToMain}
                    onChange={(event) => onSetAllowReturnToMain(event.target.checked)}
                  />
                  Allow return to main meeting
                </label>
                {assignmentMode === "self_select" && (
                  <label className="flex min-h-11 cursor-pointer items-center gap-2">
                    <input
                      type="checkbox"
                      className="size-4 rounded border-gray-400"
                      checked={allowParticipantMove}
                      onChange={(event) => onSetAllowParticipantMove(event.target.checked)}
                    />
                    Allow changing rooms
                  </label>
                )}
              </div>
              {assignmentMode === "self_select" && (
                <p className="text-sm text-gray-600">People will choose a room after you open all rooms.</p>
              )}
              {assignmentMode === "auto" && (
                <p className="text-sm text-gray-600">
                  Everyone except the meeting host is split evenly across rooms. The host stays in the main room.
                </p>
              )}
            </>
          )}
          <div className="flex flex-wrap gap-4 text-sm">
            <label className="flex flex-col gap-1 font-medium text-gray-800">
              Close countdown (seconds)
              <input
                className={`w-28 ${dialogFieldInput}`}
                type="number"
                min={0}
                max={60}
                value={countdownSeconds}
                onChange={(event) => onSetCountdownSeconds(Math.max(0, Math.min(60, Number(event.target.value) || 0)))}
              />
            </label>
            <label className="flex flex-col gap-1 font-medium text-gray-800">
              Auto-close after (minutes, 0 = off)
              <input
                className={`w-28 ${dialogFieldInput} disabled:bg-gray-100`}
                type="number"
                min={0}
                max={240}
                value={autoCloseMinutes}
                disabled={sessionStatus === "open"}
                onChange={(event) => onSetAutoCloseMinutes(Math.max(0, Math.min(240, Number(event.target.value) || 0)))}
              />
            </label>
          </div>
          <p className="text-xs leading-snug text-gray-600">
            Auto-close starts a countdown when the timer ends. Close countdown runs when you close all rooms.
          </p>
          {sessionStatus === "open" && sessionEndsAt && (
            <button type="button" className={dialogButtonSecondary} disabled={busy} onClick={onExtendSessionTimer}>
              Extend timer 5 min
            </button>
          )}
        </div>
      )}

      {(sessionStatus === "not_started" || sessionStatus === "open") && (
        <div className="overflow-hidden rounded-xl border border-gray-200">
          {rooms.length === 0 ? (
            <p className="p-6 text-center text-sm text-gray-600">No breakout rooms yet. Use Add room below.</p>
          ) : (
            rooms.map((room) => {
              const roomPeople = participantsInRoom(room.id);
              const displayName = roomNames[room.id] ?? room.name;
              const collapsed = isRoomCollapsed(room.id);
              const isRenaming = renamingRoomId === room.id;
              const swatchColor = resolveBreakoutTheme({
                id: room.id,
                name: displayName,
                themeColor: room.themeColor
              }).color;

              return (
                <div key={room.id} className="border-b border-gray-200 last:border-b-0">
                  <div className="flex flex-wrap items-center gap-2 bg-gray-50 px-3 py-2.5 sm:px-4">
                    <button
                      type="button"
                      className={dialogIconButton}
                      aria-expanded={!collapsed}
                      aria-label={collapsed ? `Expand ${displayName}` : `Collapse ${displayName}`}
                      onClick={() => onToggleRoomCollapsed(room.id)}
                    >
                      {collapsed ? <ChevronRight className="size-4" /> : <ChevronDown className="size-4" />}
                    </button>
                    {isRenaming ? (
                      <input
                        autoFocus
                        aria-label={`Rename ${displayName}`}
                        className={`min-w-[8rem] flex-1 ${dialogFieldInput}`}
                        value={roomNames[room.id] ?? room.name}
                        onChange={(event) =>
                          onSetRoomNames((current) => ({ ...current, [room.id]: event.target.value }))
                        }
                        onBlur={() => {
                          onSetRenamingRoomId(null);
                          if (sessionStatus === "open" && roomNames[room.id] !== room.name) onRenameRoom(room.id);
                        }}
                        onKeyDown={(event) => {
                          if (event.key === "Escape") onSetRenamingRoomId(null);
                          if (event.key === "Enter") {
                            event.currentTarget.blur();
                          }
                        }}
                      />
                    ) : (
                      <>
                        <span
                          className="size-4 shrink-0 rounded-[4px] border border-black/10 shadow-sm"
                          style={{ backgroundColor: swatchColor }}
                          aria-hidden
                        />
                        <span className="min-w-0 flex-1 truncate text-sm font-semibold text-gray-900">
                          {displayName}
                        </span>
                        <button
                          type="button"
                          className={roomActionIconButton}
                          title="Rename room"
                          aria-label={`Rename ${displayName}`}
                          onClick={() => onSetRenamingRoomId(room.id)}
                        >
                          <TextCursorInput className="size-4" aria-hidden />
                        </button>
                        <button
                          type="button"
                          disabled={busy}
                          className={roomDeleteIconButton}
                          title="Delete room"
                          aria-label={`Delete ${displayName}`}
                          onClick={() => onDeleteRoom(room.id)}
                        >
                          <Trash className="size-4" aria-hidden />
                        </button>
                      </>
                    )}
                    <span
                      className="ml-auto tabular-nums text-sm font-semibold text-primary"
                      aria-label={`${roomPeople.length} in room`}
                    >
                      {roomPeople.length}
                    </span>
                    {sessionStatus === "open" && (
                      <button
                        type="button"
                        className={roomActionIconButton}
                        disabled={busy}
                        title="Join room"
                        aria-label={`Join ${displayName}`}
                        onClick={() => onJoinRoom(room.id)}
                      >
                        <LogIn className="size-4" aria-hidden />
                      </button>
                    )}
                  </div>
                  {!collapsed && (
                    <ul className="divide-y divide-gray-100">
                      {roomPeople.map((person) => {
                        const name = participantDisplayName(person);
                        const joinedInRoom = sessionStatus === "open" && isParticipantJoinedInRoom(person, room);
                        const activity = findActivityForPersonInRoom(person, room.id, roomActivity);
                        return (
                          <li
                            key={person.id}
                            className="group flex flex-col gap-2 px-3 py-2.5 pl-4 sm:flex-row sm:items-center sm:pl-12"
                          >
                            <div className="flex min-w-0 flex-1 items-center gap-3">
                              <MeetUserAvatar
                                name={name}
                                avatarUrl={person.avatarUrl}
                                variant={participantAvatarVariant(person)}
                                size="sm"
                              />
                              <div className="min-w-0">
                                <p className="flex min-w-0 items-center gap-1.5 truncate text-sm font-medium text-gray-900">
                                  {joinedInRoom ? (
                                    <span
                                      className="size-2 shrink-0 rounded-full bg-green-500 ring-2 ring-green-500/25"
                                      title="In breakout room"
                                      aria-label="Currently in breakout room"
                                    />
                                  ) : null}
                                  <span className="truncate">
                                    {name}
                                    {person.isMeetingHost ? (
                                      <>
                                        {" "}
                                        <span className="inline-flex rounded bg-primary/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary">
                                          {t("participant.host")}
                                        </span>
                                      </>
                                    ) : null}
                                    {person.waitingRoomHeld ? " · waiting room" : ""}
                                    {person.isGuest ? " · guest" : ""}
                                  </span>
                                </p>
                                {activity && sessionStatus === "open" && (
                                  <BreakoutParticipantMediaIcons tracks={activity.live.tracks} className="mt-0.5" />
                                )}
                              </div>
                            </div>
                            {showMoveControls && (
                              <BreakoutParticipantRoomActions
                                person={person}
                                currentRoomId={room.id}
                                rooms={rooms}
                                roomNames={roomNames}
                                assignments={assignments}
                                eligibleParticipants={eligibleParticipants}
                                busy={busy}
                                disabled={person.waitingRoomHeld}
                                onMoveParticipant={onMoveParticipant}
                                onExchangeParticipants={onExchangeParticipants}
                                onUnassignParticipant={onUnassignParticipant}
                              />
                            )}
                          </li>
                        );
                      })}
                      {roomPeople.length === 0 && (
                        <li className="px-3 py-4 pl-12 text-sm text-gray-500">No participants assigned</li>
                      )}
                    </ul>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}

      {sessionStatus === "not_started" && assignmentMode === "manual" && unassignedParticipants().length > 0 && (
        <div className="mt-4 overflow-hidden rounded-xl border border-gray-200">
          <div className="border-b border-gray-200 bg-gray-50 px-4 py-2.5 text-sm font-semibold text-gray-900">
            Unassigned ({unassignedParticipants().length})
          </div>
          <ul className="divide-y divide-gray-100">
            {unassignedParticipants().map((person) => {
              const name = participantDisplayName(person);
              return (
                <li key={person.id} className="group flex flex-wrap items-center justify-between gap-2 px-4 py-3">
                  <div className="flex items-center gap-3">
                    <MeetUserAvatar
                      name={name}
                      avatarUrl={person.avatarUrl}
                      variant={participantAvatarVariant(person)}
                      size="sm"
                    />
                    <span className="text-sm font-medium text-gray-900">
                      {name}
                      {person.isMeetingHost ? (
                        <>
                          {" "}
                          <span className="inline-flex rounded bg-primary/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary">
                            {t("participant.host")}
                          </span>
                        </>
                      ) : null}
                    </span>
                  </div>
                  <BreakoutParticipantRoomActions
                    person={person}
                    currentRoomId=""
                    rooms={rooms}
                    roomNames={roomNames}
                    assignments={assignments}
                    eligibleParticipants={eligibleParticipants}
                    busy={busy}
                    disabled={person.waitingRoomHeld}
                    onMoveParticipant={onMoveParticipant}
                    onExchangeParticipants={onExchangeParticipants}
                    onUnassignParticipant={onUnassignParticipant}
                  />
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </>
  );
}
